import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, BellRing, Edit3, FileAudio, FileVideo, MessageSquareText, Play, Plus, RefreshCw, Save, Trash2, Volume2 } from "lucide-react";
import { api } from "../lib/api";

const eventTypes = [
  ["follow", "Follow"],
  ["raid", "Raid"],
  ["subscription", "Subscription"],
  ["gift_subscription", "Gift Subscription"],
  ["cheer", "Cheer"],
];

const stepTypes = [
  ["text", "Text on screen", MessageSquareText],
  ["tts", "Generate TTS", Volume2],
  ["audio", "Play audio", FileAudio],
  ["video", "Show video", FileVideo],
];

const blankConfig = {
  event_type: "follow",
  enabled: true,
  show_popup: false,
  popup_preset_id: "",
  steps: [],
};

const blankTest = {
  event_type: "follow",
  display_name: "Test Viewer",
  username: "testviewer",
  count: "",
  amount: "",
};

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";
const textareaClass = "w-full min-h-[88px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

function makeStep(step_type = "text") {
  const base = { id: crypto.randomUUID?.() || String(Date.now()), step_type, enabled: true };
  if (step_type === "text") {
    return {
      ...base,
      text_template: "{display_name}",
      font_size: 34,
      color: "#ffffff",
      font_family: "Inter, system-ui, sans-serif",
      font_weight: "800",
      text_align: "center",
      shadow_enabled: true,
      outline_enabled: false,
      outline_color: "#000000",
      background_enabled: false,
      background_color: "rgba(0,0,0,0.55)",
      border_radius: 0,
      padding: 0,
      placement: { x: 640, y: 360, width: 640, height: null },
    };
  }
  if (step_type === "tts") {
    return { ...base, tts_template: "Thanks {display_name}", tts_voice_id: "" };
  }
  if (step_type === "audio") {
    return { ...base, media_reference: "" };
  }
  return { ...base, media_reference: "", placement: { x: 560, y: 240, width: 800, height: 450 } };
}

function stepsFromConfig(config) {
  if (Array.isArray(config.steps) && config.steps.length) {
    return config.steps.map((step) => ({
      ...makeStep(step.step_type),
      ...step,
      placement: { ...(makeStep(step.step_type).placement || {}), ...(step.placement || {}) },
    }));
  }
  const steps = [];
  if (config.title_template) {
    steps.push({ ...makeStep("text"), text_template: config.title_template, font_size: 34, color: "#ffffff" });
  }
  if (config.message_template) {
    steps.push({ ...makeStep("text"), text_template: config.message_template, font_size: 22, color: "#e5e7eb", placement: { x: 640, y: 430, width: 720, height: null } });
  }
  if (config.media_reference) {
    steps.push({ ...makeStep("video"), media_reference: config.media_reference, placement: { x: config.x ?? 560, y: config.y ?? 240, width: config.width ?? 800, height: config.height ?? 450 } });
  }
  if (config.tts_enabled) {
    steps.push({ ...makeStep("tts"), tts_template: config.tts_template || config.message_template || "Thanks {display_name}", tts_voice_id: config.tts_voice_id || "" });
  }
  return steps.length ? steps : [makeStep("text")];
}

function formFromConfig(config) {
  return {
    event_type: config.event_type || "follow",
    enabled: config.enabled !== false,
    show_popup: config.show_popup === true,
    popup_preset_id: config.popup_preset_id || "",
    steps: stepsFromConfig(config),
  };
}

function payloadFromForm(form) {
  const firstText = form.steps.find((step) => step.enabled !== false && step.step_type === "text");
  const firstVideo = form.steps.find((step) => step.enabled !== false && step.step_type === "video" && step.media_reference);
  const firstTts = form.steps.find((step) => step.enabled !== false && step.step_type === "tts");
  return {
    event_type: form.event_type,
    enabled: Boolean(form.enabled),
    show_popup: Boolean(form.show_popup),
    popup_preset_id: form.show_popup ? (form.popup_preset_id || null) : null,
    title_template: firstText?.text_template || "{display_name}",
    message_template: firstText?.text_template || "Thanks for the support!",
    media_reference: firstVideo?.media_reference || null,
    duration: 8,
    x: firstVideo?.placement?.x ?? null,
    y: firstVideo?.placement?.y ?? null,
    width: firstVideo?.placement?.width ?? null,
    height: firstVideo?.placement?.height ?? null,
    tts_enabled: Boolean(firstTts),
    tts_voice_id: firstTts?.tts_voice_id || null,
    tts_template: firstTts?.tts_template || null,
    steps: form.steps.map((step, index) => ({ ...step, order: index + 1 })),
  };
}

function testFieldsForEvent(eventType) {
  if (eventType === "cheer") return { showCount: false, showAmount: true, amountLabel: "Bits" };
  if (eventType === "raid") return { showCount: true, showAmount: false, countLabel: "Raiders" };
  if (eventType === "gift_subscription") return { showCount: true, showAmount: false, countLabel: "Gift count" };
  if (eventType === "subscription") return { showCount: true, showAmount: false, countLabel: "Months" };
  return { showCount: false, showAmount: false };
}

function optionalInteger(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : undefined;
}

export default function Alerts() {
  const [configs, setConfigs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(blankConfig);
  const [testPayload, setTestPayload] = useState(blankTest);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [ttsStatus, setTtsStatus] = useState(null);
  const [runtimeFiles, setRuntimeFiles] = useState([]);
  const [popupPresets, setPopupPresets] = useState([]);
  const [lastTestResult, setLastTestResult] = useState(null);

  const selected = useMemo(
    () => configs.find((config) => config.id === selectedId) || null,
    [configs, selectedId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/alerts/configs");
      const next = data.configs || [];
      setConfigs(next);
      setSelectedId((current) => next.find((item) => item.id === current)?.id || next[0]?.id || "");
      try {
        const popupData = await api.get("/admin/popup-presets");
        setPopupPresets(popupData.data?.presets || []);
      } catch (popupErr) {
        setPopupPresets([]);
      }
      try {
        const fileData = await api.get("/admin/files");
        setRuntimeFiles(fileData.data?.runtime_media || []);
      } catch (fileErr) {
        setRuntimeFiles([]);
      }
      try {
        const health = await api.get("/devops/health");
        setTtsStatus(health.data?.ai_tts || null);
      } catch (statusErr) {
        setTtsStatus(null);
      }
    } catch (err) {
      setError(readError(err, "Could not load alerts."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setForm(selected ? formFromConfig(selected) : { ...blankConfig, steps: [makeStep("text")] });
  }, [selected]);

  const saveConfig = async (event) => {
    event.preventDefault();
    setBusy(selected ? "save" : "create");
    setError("");
    setMessage("");
    try {
      const payload = payloadFromForm(form);
      const { data } = selected
        ? await api.patch(`/admin/alerts/configs/${selected.id}`, payload)
        : await api.post("/admin/alerts/configs", payload);
      setConfigs((current) => {
        const exists = current.some((item) => item.id === data.id);
        return exists ? current.map((item) => (item.id === data.id ? data : item)) : [...current, data];
      });
      setSelectedId(data.id);
      setMessage(selected ? "Alert workflow saved." : "Alert workflow created.");
    } catch (err) {
      setError(readError(err, "Could not save alert."));
    } finally {
      setBusy("");
    }
  };

  const newConfig = () => {
    setSelectedId("");
    setForm({ ...blankConfig, steps: [makeStep("text")] });
    setMessage("");
    setError("");
    setLastTestResult(null);
  };

  const testAlert = async () => {
    setBusy("test");
    setError("");
    setMessage("");
    try {
      const testConfig = { ...payloadFromForm(form), event_type: testPayload.event_type || form.event_type };
      const testFields = testFieldsForEvent(testPayload.event_type || form.event_type);
      const testBody = {
        event_type: testPayload.event_type || form.event_type,
        display_name: testPayload.display_name,
        username: testPayload.username,
      };
      if (testFields.showCount) testBody.count = optionalInteger(testPayload.count) ?? 1;
      if (testFields.showAmount) testBody.amount = optionalInteger(testPayload.amount) ?? 100;
      let response;
      try {
        response = await api.post("/admin/alerts/test", { config: testConfig, ...testBody });
      } catch (inlineErr) {
        if (!selected?.id) throw inlineErr;
        response = await api.post("/admin/alerts/test", { config_id: selected.id, ...testBody });
        setMessage("Current backend tested the saved alert workflow. Save first to test your latest unsaved edits.");
      }
      const { data } = response;
      setLastTestResult(data.active || null);
      setMessage((current) => current || "Current alert workflow sent to overlay.");
    } catch (err) {
      setLastTestResult(null);
      setError(readError(err, "Could not test alert."));
    } finally {
      setBusy("");
    }
  };

  const updateStep = (index, patch) => {
    setForm((current) => ({
      ...current,
      steps: current.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)),
    }));
  };

  const removeStep = (index) => {
    setForm((current) => ({ ...current, steps: current.steps.filter((_, i) => i !== index) }));
  };

  const moveStep = (index, direction) => {
    setForm((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.steps.length) return current;
      const steps = [...current.steps];
      [steps[index], steps[nextIndex]] = [steps[nextIndex], steps[index]];
      return { ...current, steps };
    });
  };

  const addStep = (step_type) => {
    setForm((current) => ({ ...current, steps: [...current.steps, makeStep(step_type)] }));
  };
  const activeTestFields = testFieldsForEvent(testPayload.event_type);

  return (
    <div className="space-y-6" data-testid="admin-alerts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Overlay runtime</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-[hsl(var(--primary))]" />
            Stream Alerts
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Build alerts as ordered overlay steps. Text, TTS, audio, and video can be combined and tested on the existing overlay.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={newConfig} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white">
            <Plus className="w-3 h-3" /> New
          </button>
          <button onClick={load} disabled={loading || !!busy} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {(error || message) && (
        <div className={`surface p-4 text-sm ${error ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`}>{error || message}</div>
      )}

      <TtsReadiness status={ttsStatus} />

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6">
        <section className="surface overflow-hidden">
          <div className="p-5 border-b border-[hsl(var(--border))]">
            <div className="label-eyebrow">Configured alerts</div>
            <h2 className="text-lg font-semibold mt-1">Event Types</h2>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading alerts...</div>
          ) : configs.length === 0 ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No alert configs yet. Create one to test the overlay.</div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]">
              {configs.map((config) => (
                <button
                  key={config.id}
                  type="button"
                  onClick={() => setSelectedId(config.id)}
                  className={`w-full text-left p-4 hover:bg-[hsl(var(--surface-2))] transition ${selected?.id === config.id ? "bg-[hsl(var(--surface-2))]" : ""}`}
                >
                  <div className="font-medium capitalize">{config.event_type.replace("_", " ")}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {config.enabled ? "Enabled" : "Disabled"} - {(config.steps || []).length || "Legacy"} step{(config.steps || []).length === 1 ? "" : "s"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="surface p-5 space-y-6">
          <form onSubmit={saveConfig} className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-eyebrow">{selected ? "Edit alert workflow" : "Create alert workflow"}</div>
                <h2 className="text-xl font-semibold mt-1">{selected ? selected.event_type.replace("_", " ") : "New alert"}</h2>
              </div>
              <button type="submit" disabled={busy === "save" || busy === "create"} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-40">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
              <Field label="Event type" help="Which Twitch-style event this workflow is for. Test alerts use this same config without EventSub.">
                <select value={form.event_type} onChange={(event) => setForm({ ...form, event_type: event.target.value })} className={inputClass}>
                  {eventTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <label className="flex items-end gap-2 text-sm pb-2">
                <input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} className="w-4 h-4" />
                Enabled
              </label>
            </div>

            <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-4">
              <div className="label-eyebrow mb-2">Unified popup</div>
              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3 items-end">
                <label className="flex items-center gap-2 text-sm h-10">
                  <input
                    type="checkbox"
                    checked={form.show_popup}
                    onChange={(event) => setForm({ ...form, show_popup: event.target.checked })}
                    className="w-4 h-4"
                  />
                  Show popup
                </label>
                <Field label="Popup preset" help="Presets are managed in /app/popups and rendered by the shared overlay popup system.">
                  <select
                    value={form.popup_preset_id || ""}
                    onChange={(event) => setForm({ ...form, popup_preset_id: event.target.value })}
                    className={inputClass}
                    disabled={!form.show_popup}
                  >
                    <option value="">No preset selected</option>
                    {popupPresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>{preset.name}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="label-eyebrow">Alert Workflow</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  Steps run together on the overlay. Audio and video clear when playback ends; text-only alerts use an automatic readable display time.
                </p>
              </div>

              {form.steps.length === 0 && (
                <div className="border border-dashed border-[hsl(var(--border))] rounded p-4 text-sm text-[hsl(var(--muted-foreground))]">No steps added.</div>
              )}

              {form.steps.map((step, index) => (
                <AlertStepEditor
                  key={step.id}
                  step={step}
                  index={index}
                  total={form.steps.length}
                  busy={busy}
                  onChange={(patch) => updateStep(index, patch)}
                  onMove={(direction) => moveStep(index, direction)}
                  onRemove={() => removeStep(index)}
                  setBusy={setBusy}
                  setError={setError}
                  setMessage={setMessage}
                  runtimeFiles={runtimeFiles}
                  onFilesChanged={load}
                />
              ))}

              <div className="flex flex-wrap gap-2">
                {stepTypes.map(([type, label, Icon]) => (
                  <button key={type} type="button" onClick={() => addStep(type)} className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--primary))]">
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
          </form>

          <div className="rounded border border-[hsl(var(--border))] p-4 space-y-4" data-testid="alert-test-workflow-panel">
              <div>
                <div className="label-eyebrow">Local/dev admin tooling</div>
                <h3 className="text-lg font-semibold mt-1">Test Alert Workflow</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  Sends the current workflow on this page to the overlay without needing to save first.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Field label="Test event type" help="Choose the Twitch-style event you want to simulate for this overlay test.">
                  <select
                    value={testPayload.event_type}
                    onChange={(event) => setTestPayload({ ...testPayload, event_type: event.target.value })}
                    className={inputClass}
                  >
                    {eventTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Display name"><input value={testPayload.display_name} onChange={(event) => setTestPayload({ ...testPayload, display_name: event.target.value })} className={inputClass} /></Field>
                <Field label="Username"><input value={testPayload.username} onChange={(event) => setTestPayload({ ...testPayload, username: event.target.value })} className={inputClass} /></Field>
                {activeTestFields.showCount && (
                  <Field label={activeTestFields.countLabel || "Count"}>
                    <input type="number" min="1" value={testPayload.count} onChange={(event) => setTestPayload({ ...testPayload, count: event.target.value })} className={inputClass} placeholder="1" />
                  </Field>
                )}
                {activeTestFields.showAmount && (
                  <Field label={activeTestFields.amountLabel || "Amount"}>
                    <input type="number" min="1" value={testPayload.amount} onChange={(event) => setTestPayload({ ...testPayload, amount: event.target.value })} className={inputClass} placeholder="100" />
                  </Field>
                )}
              </div>
              <button
                type="button"
                onClick={testAlert}
                disabled={busy === "test" || form.steps.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm disabled:opacity-40"
                data-testid="send-test-alert-workflow"
              >
                <Play className="w-4 h-4" /> {busy === "test" ? "Sending..." : "Test current workflow"}
              </button>
              {lastTestResult && (
                <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100" data-testid="alert-test-result">
                  <div className="font-medium">Overlay alert is active.</div>
                  <div className="mt-1 text-xs text-emerald-100/80">
                    {lastTestResult.event_type} - {(lastTestResult.steps || []).length || "legacy"} step{(lastTestResult.steps || []).length === 1 ? "" : "s"} - id {lastTestResult.id}
                  </div>
                  <div className="mt-1 text-xs text-emerald-100/80">
                    OBS/browser source should be pointed at <code>/overlay</code>.
                  </div>
                </div>
              )}
            </div>
        </section>
      </div>
    </div>
  );
}

function AlertStepEditor({ step, index, total, busy, onChange, onMove, onRemove, setBusy, setError, setMessage, runtimeFiles, onFilesChanged }) {
  const fileInputRef = useRef(null);
  const [, label, TypeIcon] = stepTypes.find(([type]) => type === step.step_type) || stepTypes[0];
  const selectableFiles = (runtimeFiles || []).filter((file) => file.media_type === step.step_type);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(`upload:${step.id}`);
    setError("");
    setMessage("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/runtime-media/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      if (step.step_type === "audio" && data.media_type !== "audio") throw new Error("Choose an audio file for audio steps.");
      if (step.step_type === "video" && data.media_type !== "video") throw new Error("Choose a video file for video steps.");
      onChange({ media_reference: data.media_reference });
      setMessage(`${data.media_type === "audio" ? "Audio" : "Video"} uploaded.`);
      await onFilesChanged?.();
    } catch (err) {
      setError(err.message || readError(err, "Could not upload file."));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setBusy("");
    }
  };

  return (
    <div className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface-2))] p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-full border border-[hsl(var(--primary))] text-[hsl(var(--primary))] flex items-center justify-center text-xs font-mono shrink-0">{index + 1}</div>
          <TypeIcon className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Step {index + 1} of {total}</div>
            <div className="text-sm font-medium truncate">{label}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-1.5 text-[hsl(var(--muted-foreground))] disabled:opacity-30 hover:text-white" title="Move up"><ArrowUp className="w-4 h-4" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="p-1.5 text-[hsl(var(--muted-foreground))] disabled:opacity-30 hover:text-white" title="Move down"><ArrowDown className="w-4 h-4" /></button>
          <button type="button" onClick={onRemove} className="p-1.5 text-[hsl(var(--destructive))]" title="Remove step"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={step.enabled !== false} onChange={(event) => onChange({ enabled: event.target.checked })} className="w-4 h-4" />
        Step enabled
      </label>

      {step.step_type === "text" && (
        <div className="space-y-3">
          <Field label="Text template" help="Text shown on stream. Use {display_name}, {username}, {count}, or {amount}.">
            <textarea value={step.text_template || ""} onChange={(event) => onChange({ text_template: event.target.value })} className={textareaClass} />
          </Field>
          <div>
            <div className="label-eyebrow mb-2">Placement on 1920x1080 overlay</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumberInput label="X" value={step.placement?.x ?? 640} onChange={(x) => onChange({ placement: { ...(step.placement || {}), x } })} />
              <NumberInput label="Y" value={step.placement?.y ?? 360} onChange={(y) => onChange({ placement: { ...(step.placement || {}), y } })} />
              <NumberInput label="Width" value={step.placement?.width ?? 640} onChange={(width) => onChange({ placement: { ...(step.placement || {}), width } })} />
              <NumberInput label="Height" value={step.placement?.height ?? ""} onChange={(height) => onChange({ placement: { ...(step.placement || {}), height } })} />
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Typography</div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Field label="Font">
                <select value={step.font_family || "Inter, system-ui, sans-serif"} onChange={(event) => onChange({ font_family: event.target.value })} className={inputClass}>
                  <option value="Inter, system-ui, sans-serif">Clean sans</option>
                  <option value="Arial, Helvetica, sans-serif">Arial</option>
                  <option value="Georgia, serif">Serif</option>
                  <option value="'Trebuchet MS', system-ui, sans-serif">Rounded</option>
                  <option value="'Courier Prime', 'Courier New', monospace">Courier Prime</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif">Impact</option>
                </select>
              </Field>
              <NumberInput label="Font size" value={step.font_size ?? 34} onChange={(font_size) => onChange({ font_size })} />
              <Field label="Weight">
                <select value={step.font_weight || "800"} onChange={(event) => onChange({ font_weight: event.target.value })} className={inputClass}>
                  <option value="400">Regular</option>
                  <option value="600">Semi bold</option>
                  <option value="700">Bold</option>
                  <option value="800">Extra bold</option>
                  <option value="900">Black</option>
                </select>
              </Field>
              <Field label="Align">
                <select value={step.text_align || "center"} onChange={(event) => onChange({ text_align: event.target.value })} className={inputClass}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </Field>
              <Field label="Text color"><input type="color" value={step.color || "#ffffff"} onChange={(event) => onChange({ color: event.target.value })} className="w-full h-10 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]" /></Field>
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Effects</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <CheckControl label="Shadow" checked={step.shadow_enabled !== false} onChange={(shadow_enabled) => onChange({ shadow_enabled })} />
              <CheckControl label="Outline" checked={step.outline_enabled === true} onChange={(outline_enabled) => onChange({ outline_enabled })} />
              <Field label="Outline color"><input type="color" value={step.outline_color || "#000000"} onChange={(event) => onChange({ outline_color: event.target.value })} className="w-full h-10 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]" /></Field>
              <CheckControl label="Background" checked={step.background_enabled === true} onChange={(background_enabled) => onChange({ background_enabled })} />
            </div>
            {step.background_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <Field label="Background color">
                  <input value={step.background_color || "rgba(0,0,0,0.55)"} onChange={(event) => onChange({ background_color: event.target.value })} className={inputClass} placeholder="rgba(0,0,0,0.55)" />
                </Field>
                <NumberInput label="Padding" value={step.padding ?? 12} onChange={(padding) => onChange({ padding })} />
                <NumberInput label="Round corners" value={step.border_radius ?? 8} onChange={(border_radius) => onChange({ border_radius })} />
              </div>
            )}
          </div>
        </div>
      )}

      {step.step_type === "tts" && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3">
          <Field label="TTS template" help="What ElevenLabs should say. Use placeholders like {display_name}.">
            <textarea value={step.tts_template || ""} onChange={(event) => onChange({ tts_template: event.target.value })} className={textareaClass} />
          </Field>
          <Field label="ElevenLabs voice id" help="No API keys here, only the voice id for this step.">
            <input value={step.tts_voice_id || ""} onChange={(event) => onChange({ tts_voice_id: event.target.value })} className={inputClass} />
          </Field>
        </div>
      )}

      {(step.step_type === "audio" || step.step_type === "video") && (
        <div className="space-y-3">
          <div className="rounded border border-[hsl(var(--border))] p-3 bg-black/20">
            <div className="label-eyebrow mb-2">{step.step_type === "audio" ? "Audio file" : "Video file"}</div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <Field
                label="Select existing file"
                help="Use files already uploaded in /app/files, including files you renamed for organization."
              >
                <select
                  value={step.media_reference || ""}
                  onChange={(event) => onChange({ media_reference: event.target.value })}
                  className={inputClass}
                >
                  <option value="">No file selected</option>
                  {selectableFiles.map((file) => (
                    <option key={file.reference} value={file.reference}>
                      {file.name}
                    </option>
                  ))}
                </select>
              </Field>
              <div>
                <input ref={fileInputRef} type="file" accept={step.step_type === "audio" ? "audio/mpeg,audio/mp3,audio/wav,audio/ogg" : "video/mp4,video/webm,video/quicktime"} className="hidden" onChange={(event) => uploadFile(event.target.files?.[0])} />
                <button type="button" disabled={busy === `upload:${step.id}` || !!busy} onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 rounded bg-[hsl(var(--primary))] px-3 py-2 text-xs uppercase tracking-widest text-white disabled:opacity-50">
                  <Edit3 className="w-3 h-3" /> Upload new
                </button>
              </div>
            </div>
            <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] break-all mt-3">{step.media_reference || "No file selected yet"}</div>
          </div>
          {step.step_type === "video" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumberInput label="X" value={step.placement?.x ?? ""} onChange={(x) => onChange({ placement: { ...(step.placement || {}), x } })} />
              <NumberInput label="Y" value={step.placement?.y ?? ""} onChange={(y) => onChange({ placement: { ...(step.placement || {}), y } })} />
              <NumberInput label="Width" value={step.placement?.width ?? ""} onChange={(width) => onChange({ placement: { ...(step.placement || {}), width } })} />
              <NumberInput label="Height" value={step.placement?.height ?? ""} onChange={(height) => onChange({ placement: { ...(step.placement || {}), height } })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label = "", help = "", children } = {}) {
  return (
    <label className="block text-sm">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      {children}
      {help && <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{help}</p>}
    </label>
  );
}

function NumberInput({ label = "", value, onChange } = {}) {
  const [draft, setDraft] = useState(value ?? "");
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value ?? "");
    }
  }, [value]);

  const updateDraft = (rawValue) => {
    setDraft(rawValue);
    if (rawValue === "") {
      onChange(null);
      return;
    }
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue)) {
      onChange(numericValue);
    }
  };

  return (
    <Field label={label}>
      <input
        type="number"
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          setDraft(value ?? "");
        }}
        onChange={(event) => updateDraft(event.target.value)}
        className={inputClass}
      />
    </Field>
  );
}

function CheckControl({ label = "", checked, onChange } = {}) {
  return (
    <label className="flex items-center gap-2 text-sm h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="w-4 h-4" />
      {label}
    </label>
  );
}

function TtsReadiness({ status }) {
  if (!status) return null;
  const ready = status.tts_status === "ready";
  const message = ready
    ? "ElevenLabs TTS is ready."
    : status.tts_status === "disabled"
      ? "TTS is disabled. Set TTS_FEATURES_ENABLED=true and restart the backend."
      : "TTS is missing ElevenLabs configuration. Add ELEVENLABS_API_KEY and a voice id, then restart the backend.";
  return <div className={`surface p-4 text-sm ${ready ? "text-emerald-300" : "text-amber-200"}`}>{message}</div>;
}

function readError(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const where = Array.isArray(item.loc) ? item.loc.join(".") : "";
        return `${where ? `${where}: ` : ""}${item.msg || item.message || JSON.stringify(item)}`;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  if (err.response?.status) return `${fallback} (${err.response.status})`;
  if (err.message) return `${fallback}: ${err.message}`;
  return fallback;
}
